import { core } from '@mentimeter/http-clients';
import { useSSE } from '@mentimeter/server-sent-events';
import { useUser } from '@mentimeter/user';
import useSWR from 'swr';
import { useOnce } from '@mentimeter/react-hooks';

const ABLY_REALTIME_HOST = process.env.NEXT_PUBLIC_ABLY_REALTIME_HOST;

type PaymentServicesEvents =
  | 'all'
  | 'subscription.updated'
  | 'checkout.completed';

interface RawEventData {
  channel: string;
  data: string;
  encoding: 'json';
  id: string;
  name: string;
  timestamp: number;
}

interface EventData<T> {
  event: PaymentServicesEvents;
  payload: T;
  timestamp: number;
}

export interface SubscriptionUpdatedPayload {
  request_id: string;
}

const usePaymentServicesRealtimeToken = ({ skip }: { skip: boolean }) => {
  const { data, error } = useSWR(
    !skip ? '/payment-services/realtime-token' : null,
    async () => {
      const { data } = await core().post(`/payment-services/realtime-token`);
      return data;
    },
    {
      revalidateOnFocus: false,
      onErrorRetry: (_error, _key, _option, revalidate, { retryCount }) => {
        // Retry once and immediately upon failure.
        if (retryCount < 1) revalidate({ retryCount: retryCount + 1 });
      },
    },
  );

  return {
    token: data?.token,
    loading: !data && !error,
    error,
  };
};

export function usePaymentEvent<T = unknown>(
  event: PaymentServicesEvents,
  callback: (event: EventData<T>) => void,
) {
  const { user } = useUser();
  const { token } = usePaymentServicesRealtimeToken({ skip: !user });

  const url = `https://${ABLY_REALTIME_HOST}/event-stream?channel=payment-services:${user?.id}&v=1.2&accessToken=${token}`;

  const { start } = useSSE(
    url,
    (rawEvent) => {
      const rawEventData: RawEventData = JSON.parse(rawEvent.data);
      const eventData: EventData<T> = rawEventData?.data
        ? JSON.parse(rawEventData.data)
        : null;

      if (event === 'all' || (eventData && event === eventData.event)) {
        callback(eventData);
      }
    },
    { connectOnInit: false },
  );

  useOnce(!!token, start);
}
